import { useState } from "react";
import { StatCard } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { Locale } from "@/lib/i18n/config";
import type { OrderStatus } from "@/lib/api/types";
import {
  DISPLAY_STATUS_TONE,
  toDisplayStatus,
} from "@/modules/orders/lib/order-status-display";
import { useSellerCustomerQuery } from "@/modules/customers/services/useSellerCustomerQuery";
import { useUpdateCustomerMutation } from "@/modules/customers/services/useUpdateCustomerMutation";
import { initialsOf, tintFor } from "@/modules/customers/lib/customer-avatar";

type Props = {
  locale: Locale;
  storeSlug: string;
  customerId: string | null;
};

function formatDate(value: string | null, locale: Locale): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale === "ar" ? "ar-EG" : "en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}

function formatNumber(value: number, locale: Locale): string {
  return new Intl.NumberFormat(locale === "ar" ? "ar-EG" : "en-US").format(
    value,
  );
}

/** Currency value with a small "EGP" prefix, matching the design's stat tiles. */
function Egp({ amount, locale }: { amount: number; locale: Locale }) {
  return (
    <>
      <span className="text-[13px] font-semibold text-[color:var(--text-muted)]">
        EGP{" "}
      </span>
      {formatNumber(amount, locale)}
    </>
  );
}

/**
 * Right-hand profile pane for the Customers master–detail view. Mirrors the
 * shared design: profile header, insights (gray stat tiles), order history,
 * Tags and Contact. Email + Tags are editable (backed by PATCH
 * /seller/customers/:id); per-order history isn't returned by the API yet, so
 * that table shows an empty state.
 */
export function CustomerProfilePane({ locale, storeSlug, customerId }: Props) {
  const t = useTranslations();
  const { data: customer, isLoading } = useSellerCustomerQuery({
    storeSlug,
    customerId,
  });
  const { mutateAsync: updateCustomer, isPending } = useUpdateCustomerMutation();
  const [tagInput, setTagInput] = useState("");
  const [addingTag, setAddingTag] = useState(false);
  const [emailEditing, setEmailEditing] = useState(false);
  const [emailInput, setEmailInput] = useState("");

  if (!customerId) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-center text-sm text-[color:var(--text-muted)]">
        {t(keys.customers.selectPrompt)}
      </div>
    );
  }

  if (isLoading || !customer) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-sm text-[color:var(--text-muted)]">
        {t(keys.common.loading)}
      </div>
    );
  }

  const governorateName = customer.governorate
    ? locale === "ar"
      ? customer.governorate.nameAr
      : (customer.governorate.nameEn ?? customer.governorate.nameAr)
    : "";
  const tint = tintFor(customer.name || "?");
  const avg =
    customer.ordersCount > 0
      ? Math.round(customer.totalSpent / customer.ordersCount)
      : 0;
  const lastOrder = formatDate(customer.lastOrderAt, locale);
  const since = formatDate(customer.createdAt, locale);
  const tags = customer.tags;

  const commitTags = (next: string[]) => {
    void updateCustomer({
      storeSlug,
      customerId: customer.id,
      tags: next,
    }).catch(() => {});
  };
  const addTag = () => {
    const value = tagInput.trim();
    if (value && !tags.includes(value)) {
      commitTags([...tags, value]);
    }
    setTagInput("");
    setAddingTag(false);
  };
  const removeTag = (tag: string) => commitTags(tags.filter((x) => x !== tag));
  const saveEmail = () => {
    const value = emailInput.trim();
    void updateCustomer({
      storeSlug,
      customerId: customer.id,
      email: value || null,
    }).catch(() => {});
    setEmailEditing(false);
  };

  return (
    <div className="p-5 sm:p-6">
      {/* Profile header — gray inset */}
      <div className="rounded-[20px] border border-[color:var(--border-light)] bg-[color:var(--surface)] p-[7px]">
        <div className="flex flex-wrap items-center gap-[18px] rounded-[15px] border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] px-[22px] py-5">
          <span
            aria-hidden="true"
            className="flex h-[68px] w-[68px] shrink-0 items-center justify-center rounded-full text-[23px] font-bold"
            style={{ background: tint.bg, color: tint.fg }}
          >
            {initialsOf(customer.name || "?")}
          </span>
          <div className="min-w-0 flex-1 sm:min-w-[160px]">
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="text-[21px] font-bold tracking-[-0.015em] text-[color:var(--text-primary)]">
                {customer.name}
              </span>
            </div>
            <div className="mt-[7px] flex flex-wrap items-center gap-x-3.5 gap-y-1 text-[12.5px] text-[color:var(--text-muted)]">
              {customer.email ? (
                <span className="inline-flex min-w-0 max-w-full items-center gap-1.5">
                  <EnvelopeIcon />
                  <span className="truncate">{customer.email}</span>
                </span>
              ) : null}
              {governorateName ? (
                <span className="inline-flex items-center gap-1.5">
                  <PinIcon />
                  {governorateName}
                </span>
              ) : null}
              {since ? (
                <span className="inline-flex items-center gap-1.5">
                  <CalendarIcon />
                  {t(keys.customers.sinceLabel, { date: since })}
                </span>
              ) : null}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <a
              href={`https://wa.me/${customer.phoneE164.replace(/\D/g, "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-[10px] bg-[color:var(--wa-green)] px-[15px] py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-[color:var(--wa-green-hover)]"
            >
              <WhatsAppIcon />
              {t(keys.customers.whatsapp)}
            </a>
          </div>
        </div>
      </div>

      {/* Insights */}
      <div className="mt-4 grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(min(132px,100%),1fr))]">
        <StatCard
          label={t(keys.customers.tableOrders)}
          value={formatNumber(customer.ordersCount, locale)}
        />
        <StatCard
          label={t(keys.customers.tableTotalSpent)}
          value={<Egp amount={customer.totalSpent} locale={locale} />}
        />
        <StatCard
          label={t(keys.customers.avgOrder)}
          value={<Egp amount={avg} locale={locale} />}
        />
        <StatCard
          label={t(keys.customers.tableLastOrder)}
          value={<span className="text-[18px]">{lastOrder || "—"}</span>}
        />
      </div>

      {/* Order history + Tags/Contact */}
      <div className="mt-4 grid grid-cols-[minmax(0,1fr)] items-start gap-4 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
        {/* Order history */}
        <section className="min-w-0 overflow-hidden rounded-[18px] border border-[color:var(--border-light)] bg-[color:var(--surface)]">
          <div className="flex items-center justify-between border-b border-[color:var(--border-light)] px-[18px] py-[15px]">
            <span className="text-[14.5px] font-bold text-[color:var(--text-primary)]">
              {t(keys.customers.orderHistory)}
            </span>
            <span className="rounded-full border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] px-2.5 py-0.5 text-[12px] font-semibold text-[color:var(--text-muted)]">
              {t(keys.customers.ordersCountLabel, {
                count: customer.ordersCount,
              })}
            </span>
          </div>
          {/* Column headers — desktop grid only */}
          <div className="hidden grid-cols-[minmax(0,0.9fr)_minmax(0,1.2fr)_minmax(0,0.8fr)_minmax(0,1fr)_minmax(0,1fr)] border-b border-[color:var(--border-light)] bg-[color:var(--background-muted)] px-[18px] py-[11px] text-[11.5px] font-semibold text-[color:var(--text-muted)] lg:grid">
            <span>{t(keys.customers.colOrder)}</span>
            <span>{t(keys.customers.colDate)}</span>
            <span>{t(keys.customers.colItems)}</span>
            <span>{t(keys.customers.colTotal)}</span>
            <span className="text-end">{t(keys.customers.colStatus)}</span>
          </div>
          {customer.orders.length === 0 ? (
            <div className="px-[18px] py-10 text-center text-[13px] text-[color:var(--text-muted)]">
              {t(keys.customers.noOrders)}
            </div>
          ) : (
            customer.orders.map((order) => {
              const tone =
                DISPLAY_STATUS_TONE[
                  toDisplayStatus(order.status as OrderStatus)
                ];
              const statusPill = (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold"
                  style={{ background: tone.bg, color: tone.fg }}
                >
                  <span
                    aria-hidden="true"
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ background: tone.dot }}
                  />
                  {t(tone.labelKey)}
                </span>
              );
              return (
                <div
                  key={order.id}
                  className="border-b border-[color:var(--hair)] px-[18px] py-3 last:border-b-0"
                >
                  {/* Mobile / narrow: stacked */}
                  <div className="flex items-center justify-between gap-3 lg:hidden">
                    <div className="min-w-0">
                      <div className="truncate text-[13px] font-semibold text-[color:var(--text-primary)]">
                        {order.orderCode}
                      </div>
                      <div className="mt-0.5 text-[11.5px] text-[color:var(--text-muted)]">
                        {formatDate(order.createdAt, locale)} ·{" "}
                        {formatNumber(order.itemsCount, locale)}{" "}
                        {t(keys.customers.colItems)}
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1.5">
                      <span className="text-[13px] font-bold text-[color:var(--text-primary)]">
                        <Egp amount={order.totalAmount} locale={locale} />
                      </span>
                      {statusPill}
                    </div>
                  </div>
                  {/* Desktop: grid */}
                  <div className="hidden grid-cols-[minmax(0,0.9fr)_minmax(0,1.2fr)_minmax(0,0.8fr)_minmax(0,1fr)_minmax(0,1fr)] items-center text-[13px] lg:grid">
                    <span className="truncate font-semibold text-[color:var(--text-primary)]">
                      {order.orderCode}
                    </span>
                    <span className="text-[color:var(--text-secondary)]">
                      {formatDate(order.createdAt, locale)}
                    </span>
                    <span className="text-[color:var(--text-secondary)]">
                      {formatNumber(order.itemsCount, locale)}
                    </span>
                    <span className="font-semibold text-[color:var(--text-primary)]">
                      <Egp amount={order.totalAmount} locale={locale} />
                    </span>
                    <span className="flex justify-end">{statusPill}</span>
                  </div>
                </div>
              );
            })
          )}
        </section>

        {/* Tags + Contact */}
        <div className="flex min-w-0 flex-col gap-4">
          <section className="rounded-[18px] border border-[color:var(--border-light)] bg-[color:var(--surface)] px-[18px] py-4">
            <div className="mb-3 text-[13.5px] font-bold text-[color:var(--text-primary)]">
              {t(keys.customers.tagsTitle)}
            </div>
            <div className="flex flex-wrap gap-[7px]">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] py-1.5 pe-1.5 ps-2.5 text-[12px] font-semibold text-[color:var(--text-secondary)]"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    disabled={isPending}
                    aria-label={`${t(keys.common.delete)} ${tag}`}
                    className="inline-flex h-7 w-7 items-center justify-center rounded text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--hover)] hover:text-[color:var(--text-primary)] disabled:opacity-50 lg:h-4 lg:w-4"
                  >
                    <CloseIcon />
                  </button>
                </span>
              ))}
              {addingTag ? (
                <input
                  autoFocus
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") addTag();
                    if (e.key === "Escape") {
                      setTagInput("");
                      setAddingTag(false);
                    }
                  }}
                  onBlur={addTag}
                  placeholder={t(keys.customers.addTag)}
                  className="w-[110px] rounded-lg border border-[color:var(--border-strong)] bg-[color:var(--surface)] px-2.5 py-1.5 text-[12px] text-[color:var(--text-primary)] outline-none"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setAddingTag(true)}
                  disabled={isPending}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-[color:var(--border-strong)] px-2.5 py-1.5 text-[12px] font-semibold text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--surface-alt)] disabled:opacity-50"
                >
                  <PlusIcon />
                  {t(keys.customers.addTag)}
                </button>
              )}
            </div>
          </section>

          <section className="rounded-[18px] border border-[color:var(--border-light)] bg-[color:var(--surface)] px-[18px] py-4">
            <div className="mb-3 text-[13.5px] font-bold text-[color:var(--text-primary)]">
              {t(keys.customers.detailContactTitle)}
            </div>
            <div className="flex flex-col gap-3.5">
              {/* Email — editable */}
              <div className="flex items-center gap-[11px]">
                <span
                  aria-hidden="true"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] text-[color:var(--text-muted)]"
                >
                  <EnvelopeIcon />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-medium text-[color:var(--text-muted)]">
                    {t(keys.common.email)}
                  </div>
                  {emailEditing ? (
                    <input
                      autoFocus
                      type="email"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveEmail();
                        if (e.key === "Escape") setEmailEditing(false);
                      }}
                      onBlur={saveEmail}
                      placeholder="name@email.com"
                      className="mt-0.5 w-full rounded-md border border-[color:var(--border-strong)] bg-[color:var(--surface)] px-2 py-1 text-[13px] text-[color:var(--text-primary)] outline-none"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setEmailInput(customer.email ?? "");
                        setEmailEditing(true);
                      }}
                      className="block w-full truncate text-start text-[13px] font-semibold text-[color:var(--text-primary)] hover:text-[color:var(--info-fg)]"
                    >
                      {customer.email || (
                        <span className="font-normal text-[color:var(--text-muted)]">
                          {t(keys.customers.addTag)}
                        </span>
                      )}
                    </button>
                  )}
                </div>
              </div>
              <ContactRow
                icon={<PhoneIcon />}
                label={t(keys.common.phone)}
                value={<bdi dir="ltr">{customer.phoneE164}</bdi>}
              />
              <ContactRow
                icon={<PinIcon />}
                label={t(keys.customers.detailAddress)}
                value={customer.lastAddress ?? ""}
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function ContactRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-[11px]">
      <span
        aria-hidden="true"
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] text-[color:var(--text-muted)]"
      >
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-medium text-[color:var(--text-muted)]">
          {label}
        </div>
        <div className="min-h-[1.2em] truncate text-[13px] font-semibold text-[color:var(--text-primary)]">
          {value}
        </div>
      </div>
    </div>
  );
}

/* ── Icons ─────────────────────────────────────────────────────────── */

const S = {
  fill: "none",
  stroke: "currentColor" as const,
  strokeWidth: 1.7,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

function EnvelopeIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" {...S}>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-10 5L2 7" />
    </svg>
  );
}
function PhoneIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" {...S}>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92Z" />
    </svg>
  );
}
function PinIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" {...S}>
      <path d="M21 10c0 7-9 12-9 12s-9-5-9-12a9 9 0 0 1 18 0Z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}
function CalendarIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" {...S}>
      <rect x="3" y="4" width="18" height="18" rx="2.5" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}
function WhatsAppIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2a10 10 0 0 0-8.6 15l-1.3 4.7 4.8-1.3A10 10 0 1 0 12 2Zm5.3 14.1c-.2.6-1.3 1.2-1.8 1.2-.5.1-1 .1-1.7-.1-.4-.2-1-.4-1.7-.7-2.9-1.3-4.8-4.3-5-4.5-.1-.2-1.1-1.5-1.1-2.8s.7-2 .9-2.2c.2-.3.5-.3.7-.3h.5c.2 0 .4 0 .6.5l.8 1.9c.1.2 0 .4-.1.5l-.3.4c-.2.2-.3.3-.1.6.1.3.7 1.1 1.4 1.8.9.8 1.7 1.1 2 1.2.2.1.4.1.6-.1l.7-.8c.2-.2.4-.2.6-.1l1.8.9c.2.1.4.2.4.3.1.2.1.7-.1 1.4Z" />
    </svg>
  );
}
function PlusIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" {...S} strokeWidth={2}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}
function CloseIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" {...S} strokeWidth={2.2}>
      <path d="M6 6l12 12M6 18L18 6" />
    </svg>
  );
}
